import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Device, DeviceInfo } from '@capacitor/device';
import { combineLatest, lastValueFrom, of } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { catchError, map } from 'rxjs/operators';
import packageJson from '../../../../package.json';
import { AppConfigModel } from '../models/config/app-config';
import { SessionService } from '../state-mangement/session/session.service';
import { StorageKeys } from '../models/storage-keys';
import { LocalStorageService } from './local-storage.service';

/**
 * Loads App config file on startup.
 */
@Injectable({ providedIn: 'root' })
export class AppConfig {
  static settings: AppConfigModel;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private sessionService: SessionService
  ) {}

  load() {
    return this.loadConfigJSONIIS();
  }

  private loadConfigJSONIIS() {
    const jsonFile = 'assets/config/config.json';

    const fileRequest = this.http
      .get<AppConfigModel>(jsonFile)
      .pipe(catchError(() => of({} as AppConfigModel)));

    const infoStream = combineLatest([
      fileRequest,
      fromPromise<DeviceInfo>(Device.getInfo()),
      this.localStorageService.getKey(StorageKeys.server),
    ]).pipe(
      map(([config, deviceInfo, server]) => {
        const version = Number(packageJson.version.split('.')[0]);
        const minor = Number(packageJson.version.split('.')[1]);

        if (deviceInfo.platform === 'web') {
          const overridden = server ? { backend: { url: server } } : {};
          return {
            ...config,
            ...overridden,
            version,
            minorVersion: minor,
          } as AppConfigModel;
        } else {
          this.sessionService.nativeDevice(true);
          return {
            backend: { url: server },
            version,
            minorVersion: minor,
          } as AppConfigModel;
        }
      })
    );

    return new Promise<void>((resolve, reject) => {
      lastValueFrom(infoStream)
        .then((response) => {
          AppConfig.settings = response;
          return this.localStorageService.setKey(
            'Backend',
            response.backend.url
          );
        })
        .then(() => resolve())
        .catch((error) =>
          reject(`Could not load file '${jsonFile}': ${JSON.stringify(error)}`)
        );
    });
  }
}
